"use client";

import React, { useState } from "react";
import Modal from "Common/Components/Modal";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Plus } from "lucide-react";
import { useAppServices } from "hook/services";
import { useParams } from "react-router-dom";
import Select from "react-select";
interface propTypes {
  handleRefresh: () => void;
  users: {
    label: string;
    value: string;
  }[];
}

const TaskCreateModal = ({ handleRefresh, users }: propTypes) => {
  const AppService = useAppServices();
  const { locationId, contactId } = useParams(); // ✅ from params

  const [show, setShow] = useState<boolean>(false);
  const toggle = () => setShow((prev) => !prev);

  const handleSubmit = async (values: any) => {
    const payload = {
      ...values,
      locationId,
      contactId,
    };

    const { response } = await AppService.task.create({
      payload,
      toaster: true,
    });

    if (response) {
      formik.resetForm();
      toggle();
      handleRefresh();
    }
  };

  // ✅ Formik setup based on Task schema
  const formik = useFormik({
    initialValues: {
      title: "",
      description: "",
      dueDate: "",
      completed: false,
      assignedTo: null,
    },
    validationSchema: Yup.object({
      title: Yup.string()
        .trim()
        .min(2, "Task title must be at least 2 characters long")
        .max(200, "Task title must be less than 200 characters")
        .required("Please enter a task title"),

      description: Yup.string()
        .trim()
        .max(2000, "Description must be less than 2000 characters"),

      dueDate: Yup.date()
        .required("Please select a due date")
        .typeError("Invalid date format"),

      assignedTo: Yup.string().nullable(),
      completed: Yup.boolean(),
    }),
    onSubmit: (values) => handleSubmit(values),
  });

  return (
    <>
      {/* Add Task Button */}
      <button
        type="button"
        className="text-white btn btn-sm bg-custom-500 border-custom-500 hover:bg-custom-600 focus:ring focus:ring-custom-100 dark:focus:ring-custom-400/20"
        onClick={toggle}
      >
        <Plus className="inline-block size-3 mr-1" />
        <span className="align-middle text-xs">Add</span>
      </button>

      {/* Modal */}
      <Modal
        show={show}
        onHide={toggle}
        id="taskModal"
        modal-center="true"
        className="fixed flex flex-col transition-all duration-300 ease-in-out left-2/4 z-drawer -translate-x-2/4 -translate-y-2/4"
        dialogClassName="w-screen md:w-[35rem] bg-white dark:bg-zink-700 shadow rounded-md dark:shadow-zink-900/50"
      >
        <Modal.Header className="flex items-center justify-between p-4 border-b dark:border-zink-500/20">
          <Modal.Title className="text-16 font-semibold text-zink-800 dark:text-zink-100">
            Add New Task
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="max-h-[calc(theme('height.screen')_-_180px)] p-4 overflow-y-auto">
          <form onSubmit={formik.handleSubmit} className="space-y-4">
            {/* Title */}
            <div>
              <label className="block mb-1 text-sm font-medium text-zink-800 dark:text-zink-100">
                Title
              </label>
              <input
                type="text"
                name="title"
                placeholder="Enter task title"
                className="form-input w-full dark:bg-zink-600 dark:border-zink-500 dark:text-zink-100 dark:placeholder:text-zink-400"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.title}
              />
              {formik.touched.title && formik.errors.title && (
                <p className="text-red-400 dark:text-red-300 text-sm mt-1">
                  {formik.errors.title}
                </p>
              )}
            </div>

            {/* Description */}
            <div>
              <label className="block mb-1 text-sm font-medium text-zink-800 dark:text-zink-100">
                Description
              </label>
              <textarea
                name="description"
                placeholder="Enter task description (optional)"
                className="form-textarea w-full h-24 dark:bg-zink-600 dark:border-zink-500 dark:text-zink-100 dark:placeholder:text-zink-400"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.description}
              />
              {formik.touched.description && formik.errors.description && (
                <p className="text-red-400 dark:text-red-300 text-sm mt-1">
                  {formik.errors.description}
                </p>
              )}
            </div>

            {/* Due Date */}
            <div>
              <label className="block mb-1 text-sm font-medium text-zink-800 dark:text-zink-100">
                Due Date
              </label>
              <input
                type="date"
                name="dueDate"
                className="form-input w-full dark:bg-zink-600 dark:border-zink-500 dark:text-zink-100"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.dueDate}
              />
              {formik.touched.dueDate && formik.errors.dueDate && (
                <p className="text-red-400 dark:text-red-300 text-sm mt-1">
                  {formik.errors.dueDate}
                </p>
              )}
            </div>

            {/* Assigned To */}
            <div>
              <label className="block mb-1 text-sm font-medium text-zink-800 dark:text-zink-100">
                Assigned To (User ID)
              </label>
              <Select
                name="assignedTo"
                value={
                  users.find(
                    (option) => option.value === formik.values.assignedTo
                  ) || null
                }
                onChange={(selected) =>
                  formik.setFieldValue(
                    "assignedTo",
                    selected ? selected.value : null
                  )
                }
                options={users}
                placeholder="Assign to (optional)"
                isClearable
              />
              {formik.touched.assignedTo && formik.errors.assignedTo && (
                <p className="text-red-400 dark:text-red-300 text-sm mt-1">
                  {formik.errors.assignedTo}
                </p>
              )}
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                className="text-red-500 dark:text-red-400 bg-white dark:bg-zink-600 border border-slate-200 dark:border-zink-500 btn hover:text-red-600 dark:hover:text-red-300 hover:bg-slate-50 dark:hover:bg-zink-500"
                onClick={() => {
                  formik.resetForm();
                  toggle();
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="text-white btn bg-custom-500 border-custom-500 hover:bg-custom-600 dark:bg-custom-500 dark:border-custom-500 dark:hover:bg-custom-600"
              >
                Add Task
              </button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default TaskCreateModal;
